import {
  Task,
  TaskSearchStatus,
  TaskSearchType,
  searchTasks,
} from '@admiin-com/ds-graphql';
import { WBBox, WBList, useMediaQuery, useTheme } from '@admiin-com/ds-web';
import { useMemo } from 'react';
import { gql, useQuery } from '@apollo/client';
import ToolBoxIcon from '../../../../../../assets/icons/task.svg';
import ClientsIcon from '../../../../../../assets/icons/client.svg';
import UserIcon from '../../../../../../assets/icons/contacts.svg';
import AdmiinAccountIcon from '../../../../../../assets/icons/admiin-account.svg';
import ServicesIcon from '../../../../../../assets/icons/taskbox.svg';
import DocumentsIcon from '../../../../../../assets/icons/templates.svg';
import EmailIcon from '../../../../../../assets/icons/email.svg';
import { useTranslation } from 'react-i18next';
import { useFirmId } from '../../../../../hooks/useClientWorkflow/useClientWorkflow';
import { PATHS } from '../../../../../navigation/paths';
import { CreateTaskButton } from '../CreateTaskButton';
import { NavItem } from './NavItem';
import HomeIcon from '../../../../../../assets/icons/home.svg';
import { Theme } from '@mui/system';
import { Zoom, Skeleton, ListItem } from '@mui/material';
import {
  useCurrentEntityId,
  useSelectedEntity,
} from '../../../../../hooks/useSelectedEntity/useSelectedEntity';
import { useFeatureFlags } from '../../../../../hooks/useFeatureFlags/useFeatureFlags';
import { usePaymentsEnabledOwnEntity } from '../../../../hooks/usePaymentsEnabledOwnEntity/usePaymentsEnabledOwnEntity';
import {
  isDeclinedTask,
  isPayoutFailedTask,
} from '../../../../../helpers/tasks';
import {
  hasOverDueInstallmentForSent,
  isPayIDTask,
} from '../../../../../helpers/payments';
import useTaskSearchFilters from '../../../../hooks/useTaskSearchFilters/useTaskSearchFilters';
import useSearchTasks from '../../../../hooks/useTasksFromUrl/useSearchTasks';
import {
  useCurrentUser,
  useUserId,
} from '../../../../../hooks/useCurrentUser/useCurrentUser';

const SKELETON_COUNT = 6; // Maximum possible menu items
export interface NavListProps {
  onNavigated?: () => void;
  onCreateTask?: () => void;
  opened?: boolean;
}

export function NavList({ onCreateTask, onNavigated, opened }: NavListProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const user = useCurrentUser();
  const loading = user.__loading;

  const { filters } = useTaskSearchFilters();
  const entityId = useCurrentEntityId();

  const { tasks } = useSearchTasks({
    entityId: entityId,
    type: TaskSearchType.ALL,
  });

  const hasDeclinedPayment = tasks.find(
    (task: Task) =>
      isDeclinedTask(task) ||
      isPayoutFailedTask(task) ||
      hasOverDueInstallmentForSent(task)
  );

  const hasPendingWalletTransfer = tasks.some((task: Task) =>
    isPayIDTask(task)
  );

  const { firmId } = useFirmId();
  const isFirm = !!firmId;

  const userId = useUserId();
  const { data: reviewData } = useQuery(gql(searchTasks), {
    variables: {
      firmId,
      status: TaskSearchStatus.REVIEW,
      type: TaskSearchType.ALL,
    },
    fetchPolicy: 'cache-and-network',
    skip: !firmId,
  });

  const myReviewCount = useMemo(() => {
    const items = reviewData?.searchTasks?.items || [];
    return items.filter(
      (task: any) =>
        task.status === TaskSearchStatus.REVIEW && task.managerId === userId
    ).length;
  }, [reviewData, userId]);

  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('md')
  );

  const transitionDuration = {
    enter: theme.transitions.duration.enteringScreen,
    exit: theme.transitions.duration.leavingScreen,
  };
  const { entity } = useSelectedEntity();
  const { featureFlags } = useFeatureFlags();
  //const paymentEnabled = usePaymentEnabled();
  const paymentEnabled = usePaymentsEnabledOwnEntity();

  // Show skeleton loaders during initial load
  if (loading && (!user || !user.id)) {
    return (
      <WBBox
        sx={{
          backgroundColor: theme.palette.background.paper,
          display: 'flex',
          flexDirection: 'column',
          height: { xs: undefined, sm: '100%' },
          py: 2,
          flex: 1,
          px: opened ? [2, 1] : 'inherit',
          borderRight: `1px solid ${theme.palette.divider}`,
        }}
      >
        <WBList sx={{ flexGrow: 1, marginTop: [0, 2] }}>
          {Array.from({ length: SKELETON_COUNT }).map((_, index) => (
            <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
              <Skeleton
                variant="rectangular"
                width="100%"
                height={40}
                sx={{ borderRadius: 1, mx: 1 }}
              />
            </ListItem>
          ))}
        </WBList>
      </WBBox>
    );
  }

  return (
    <WBBox
      sx={{
        backgroundColor: theme.palette.background.paper,
        display: 'flex',
        flexDirection: 'column',
        height: { xs: undefined, sm: '100%' },
        py: 2,
        flex: 1,
        px: opened ? [2, 1] : 'inherit',
        borderRight: `1px solid ${theme.palette.divider}`,
      }}
    >
      {opened && !isMobile && !isFirm && (
        <Zoom
          in={opened && !isMobile}
          timeout={transitionDuration}
          style={{
            transitionDelay: `${
              opened && !isMobile ? transitionDuration.exit : 0
            }ms`,
          }}
          unmountOnExit
        >
          <CreateTaskButton onClick={onCreateTask} />
        </Zoom>
      )}

      <WBList sx={{ flexGrow: 1, marginTop: [0, 2] }}>
        {isFirm && (
          <>
            <NavItem
              noTitle={!opened}
              onClick={() => {
                onNavigated && onNavigated();
              }}
              title={t('Home', { ns: 'common' })}
              icon={HomeIcon}
              path={PATHS.home}
            />
            <NavItem
              onClick={() => {
                onNavigated && onNavigated();
              }}
              title={t('clientsTitle', { ns: 'common' })}
              icon={ClientsIcon}
              path={PATHS.clients}
              noTitle={!opened}
            />
            <NavItem
              onClick={() => {
                onNavigated && onNavigated();
              }}
              title={t('review', { ns: 'common' })}
              icon={EmailIcon}
              noTitle={!opened}
              path={PATHS.inbox}
              badgeCount={myReviewCount}
            />
          </>
        )}
        {/*{!isFirm && (*/}
        {!isFirm && (
          <NavItem
            onClick={() => {
              onNavigated && onNavigated();
            }}
            hasBadge={hasDeclinedPayment || hasPendingWalletTransfer}
            badgeColor={hasDeclinedPayment ? 'error' : 'warning'}
            title={t('taskboxTitle', { ns: 'common' })}
            icon={ToolBoxIcon}
            path={PATHS.tasks}
            noTitle={!opened}
          />
        )}
        {/*)}*/}
        {/*{isFirm ? (*/}
        {/*  <NavItem*/}
        {/*    onClick={() => {*/}
        {/*      onNavigated && onNavigated();*/}
        {/*    }}*/}
        {/*    title={t('clientsTitle', { ns: 'common' })}*/}
        {/*    icon={ClientsIcon}*/}
        {/*    path={PATHS.clients}*/}
        {/*    noTitle={!opened}*/}
        {/*  />*/}
        {/*) : null}*/}

        {!isFirm && (
          <NavItem
            onClick={() => {
              onNavigated && onNavigated();
            }}
            title={t('contactsTitle', { ns: 'common' })}
            icon={UserIcon}
            path={PATHS.contacts}
            noTitle={!opened}
          />
        )}

        {entity?.taxNumber && (!isFirm || (isFirm && paymentEnabled)) && (
          <NavItem
            onClick={() => {
              onNavigated && onNavigated();
            }}
            title={t('servicesTitle', { ns: 'common' })}
            icon={ServicesIcon}
            noTitle={!opened}
            path={PATHS.services}
          />
        )}
        {entity?.isFirm && (
          <NavItem
            onClick={() => {
              onNavigated && onNavigated();
            }}
            title={t('Documents', { ns: 'common' })}
            icon={DocumentsIcon}
            noTitle={!opened}
            path={PATHS.documents}
          />
        )}
        {featureFlags?.admiinAccount &&
          entity?.taxNumber &&
          (!isFirm || (isFirm && paymentEnabled)) && (
            <NavItem
              onClick={() => {
                onNavigated && onNavigated();
              }}
              title={t('admiinAccountTitle', { ns: 'common' })}
              icon={AdmiinAccountIcon}
              path={PATHS.admiinAccount}
              noTitle={!opened}
            />
          )}
        {/*{isFirm && <NavItem*/}
        {/*  onClick={() => {*/}
        {/*    onNavigated && onNavigated();*/}
        {/*  }}*/}
        {/*  hasBadge={hasDeclinedPayment || hasPendingWalletTransfer}*/}
        {/*  badgeColor={hasDeclinedPayment ? 'error' : 'warning'}*/}
        {/*  title={t('myfirm', { ns: 'common' })}*/}
        {/*  icon={ToolBoxIcon}*/}
        {/*  path={PATHS.tasks}*/}
        {/*  noTitle={!opened}*/}
        {/*/>}*/}
      </WBList>
    </WBBox>
  );
}

export default NavList;
